<template>
  <!-- v-if="(!$isServer && !canRecordInterview) || isXiaomi" -->
  <ClientOnly>
    <VacancyContainer
      v-if="!canRecordInterview"
      :id="vacancyId"
      v-slot="{ company }"
      @ready="handleVacancyContainerReady"
    >
      <VRow justify="center">
        <VCol cols="12" md="8" xl="7">
          <CompanySmallCard v-if="$vuetify.breakpoint.smAndDown" class="mb-4" :company="company" />
          <AppIosMessage v-if="isIOS" :vacancy-id="vacancyId" :candidate="candidate" />
          <VCard v-else class="mt-6">
            <CanNotRecordInterview :cancel-text="$t('back')" @cancel="$router.go(-2)">
              <template #description>
                <VRow justify="center">
                  <VCol cols="12" md="8">
                    <div class="text-center">
                      {{ $t('browserListTip') }}:
                    </div>
                  </VCol>
                </VRow>
              </template>
              <template #buttons>
                <VRow justify="center">
                  <VCol cols="12 d-flex flex-column align-center">
                    <VDivider width="90%" class="mb-4" />
                    <VBtn color="success" class="px-4 my-6 center" @click="$router.go(-2)">
                      {{ $t('back') }}
                    </VBtn>
                  </VCol>
                </VRow>
              </template>
            </CanNotRecordInterview>
          </VCard>
        </VCol>
      </VRow>
    </VacancyContainer>
    <AppPreloader v-else-if="loading" />
    <VacancyContainer
      v-else
      :id="vacancyId"
      v-slot="{ company, vacancy }"
      @ready="handleVacancyContainerReady"
    >
      <VStepper
        v-if="!isDone && !isInterrupted"
        v-model="currentStep"
        alt-labels
        class="transparent elevation-0"
      >
        <VRow justify="center">
          <VCol
            cols="12"
            :md="$route.meta.iframe ? 10 : 8"
            :xl="$route.meta.iframe ? 10 : 6"
            :class="{'pa-0': $route.meta.iframe}"
          >
            <VRow justify="center">
              <VCol cols="12" lg="10" :class="{'pb-0': $route.meta.iframe}">
                <VStepperHeader class="elevation-0 caption text-center stepper-header">
                  <VStepperStep step="1">
                    {{ $t('cameraOn') }}
                  </VStepperStep>
                  <VDivider />
                  <VStepperStep step="2">
                    {{ $t('micSettings') }}
                  </VStepperStep>
                  <VDivider />
                  <VStepperStep step="3">
                    {{ $t('cameraSettings') }}
                  </VStepperStep>
                  <VDivider />
                  <VStepperStep step="4">
                    {{ $t('interview') }}
                  </VStepperStep>
                </VStepperHeader>
              </VCol>
            </VRow>
          </VCol>
        </VRow>
        <VRow justify="center">
          <VCol
            cols="12"
            :md="$route.meta.iframe ? 10 : currentStep === 4 ? 12 : 8"
            :xl="$route.meta.iframe ? 10 : currentStep === 4 ? 12 : 6"
            :class="{'pt-0': $route.meta.iframe}"
          >
            <VCard
              :elevation="$route.meta.iframe || currentStep === 4 ? 0 : 1"
              :color="$route.meta.iframe && currentStep === 1 || currentStep === 4 ? 'transparent' : '#ffffff'"
              :class="$route.meta.iframe && currentStep === 1 ? 'pa-0 pa-md-0 mb-0' : 'pa-3 pa-md-5 px-md-12 mb-5'"
            >
              <PrefetchInterview v-if="vacancy" :vacancy="vacancy" />
              <VStepperItems class="full-screen-item">
                <VStepperContent :step="0" class="pa-0">
                  <CandidateProfileForm
                    :vacancy="vacancy"
                    :candidate="candidate"
                    @done="setStep(2)"
                  />
                </VStepperContent>
                <VStepperContent :step="1" class="pa-0">
                  <InterviewRequestPermissions v-if="currentStep === 1" @done="permissionsGranted" />
                  <span v-else />
                </VStepperContent>
                <VStepperContent :step="2" class="pa-0">
                  <InterviewMicrophoneCheck
                    v-if="currentStep === 2"
                    @done="setStep(3)"
                  />
                  <span v-else />
                </VStepperContent>
                <AppWithLoading v-slot:default="{loader}">
                  <VStepperContent :step="3" class="pa-0">
                    <InterviewCameraCheck v-if="currentStep === 3" @done="loader(() => setInterviewStep(4))" />
                    <span v-else />
                  </VStepperContent>
                </AppWithLoading>
                <VStepperContent :step="4" class="pa-0">
                  <VRow justify="center">
                    <VCol cols="12" md="10" xl="8" class="relative">
                      <ClientOnly>
                        <h1 class="d-none headline text-center mb-6">
                          {{ $t('interviewRecord') }} <i class="dot" />
                        </h1>
                        <div v-if="company.logo" class="interview-avatar">
                          <CompanyAvatar :company="company" :size="50" />
                        </div>
                        <InterviewRecorder
                          v-if="currentStep === 4"
                          :candidate="candidate"
                          :interviewer="vacancy.script.interviewer"
                          :questions="vacancy.script.questions"
                          :vacancy-id="vacancyId"
                          @stopped="stopped"
                          @interrupted="interrupted"
                        />
                        <div class="d-flex">
                          <VBtn
                            v-if="iframeInterviewDone"
                            color="success"
                            class="mx-auto my-4"
                            dense
                            @click="completeIframeInterview"
                          >
                            {{ $t('close') }}
                          </VBtn>
                        </div>
                      </ClientOnly>
                    </VCol>
                  </VRow>
                </VStepperContent>
              </VStepperItems>
            </VCard>
          </VCol>
        </VRow>
      </VStepper>
      <InterviewDone v-else-if="isDone" :company="company" :vacancy="vacancy" />
      <InterviewInterrupted v-else-if="isInterrupted" />
      <Portal to="support">
        <SupportTicketDialog v-if="$route.meta.supportBtn" :candidate="candidate" />
      </Portal>
    </VacancyContainer>
  </ClientOnly>
</template>

<script>
import ClientOnly from 'vue-client-only';
import LogRocket from 'logrocket';
import CanNotRecordInterview from '@huntica/huntica-interview-module/src/components/CanNotRecordInterview.vue';
import { canRecordInterview } from '@huntica/huntica-interview-module/src/helpers';
import { initLogRocket } from '@/logrocket';
import PrefetchInterview from '@huntica/huntica-interview-module/src/components/PrefetchInterview.vue';
import AppIosMessage from '@/components/AppIosMessage.vue';
import VacancyContainer from '../components/Vacancy/VacancyContainer.vue';
import CompanySmallCard from '../components/Company/CompanySmallCard.vue';
import CompanyAvatar from '../components/Company/CompanyAvatar.vue';
import CandidateProfileForm from '../forms/CandidateProfileForm.vue';
import InterviewDone from '../components/Interview/InterviewDone.vue';
import InterviewInterrupted from '../components/Interview/InterviewInterrupted.vue';
import { candidateStatuses } from '../enums';
import { isIOS, safeGet } from '../functions';
import useLocale from '@/compositions/useLocale';
import AppPreloader from '../components/AppPreloader.vue';
import SupportTicketDialog from '../components/SupportTicketDialog.vue';
import RecaptchaMixin from '../mixins/RecaptchaMixin';
import AppWithLoading from '../components/AppWithLoading';

export default {
  name: 'InterviewTest',
  components: {
    AppWithLoading,
    AppIosMessage,
    AppPreloader,
    CanNotRecordInterview,
    CompanySmallCard,
    CompanyAvatar,
    ClientOnly,
    InterviewDone,
    InterviewInterrupted,
    CandidateProfileForm,
    VacancyContainer,
    PrefetchInterview,
    InterviewRequestPermissions: process.browser
      ? () => import('../components/Interview/InterviewRequestPermissions.vue') : null,
    InterviewMicrophoneCheck: process.browser
      ? () => import('../components/Interview/InterviewMicrophoneCheck.vue') : null,
    InterviewCameraCheck: process.browser
      ? () => import('../components/Interview/InterviewCameraCheck.vue') : null,
    SupportTicketDialog,
    InterviewRecorder: process.browser
      ? () => import('../components/Interview/InterviewRecorder.vue')
      : null,
  },
  mixins: [RecaptchaMixin],
  setup() {
    const { setLocaleFromQuery } = useLocale();
    return {
      setLocaleFromQuery,
    };
  },
  metaInfo() {
    return {
      title: this.pageTitle,
    };
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    step: {
      type: Number,
      required: false,
      default: 1,
    },
  },
  data() {
    return {
      process: process.browser,
      loading: true,
      pageTitle: null,
      canRecordInterview: process.browser && canRecordInterview(),
      currentStep: 1,
      isDone: false,
      isInterrupted: false,
      globalStream: null,
      iframeInterviewDone: false,
    };
  },
  computed: {
    candidate() {
      const candidate = safeGet(this.$store.state.candidate, ['data', 'candidate']);

      if (!candidate || candidate.vacancyId !== this.vacancyId) {
        return null;
      }

      return candidate;
    },
    candidateVacancy() {
      if (!this.candidate) {
        return null;
      }
      return safeGet(this.$store.state.candidate, ['data', 'vacancy']);
    },
    isIOS() {
      return isIOS();
    },
    IOSCode() {
      return this.candidate ? this.candidate.hashId : this.vacancyId;
    },
  },
  watch: {
    currentStep(step) {
      this.$router.push({
        name: this.$route.name,
        params: { vacancyId: this.vacancyId, step },
        query: { token: localStorage.getItem(`vacancy_${this.vacancyId}_current_candidate`) },
      });
    },
  },
  beforeCreate() {
    initLogRocket(this.$route.query.disableLogRocket);
  },
  async created() {
    if (localStorage.getItem(`vacancy_${this.vacancyId}_current_candidate`)) {
      await this.$store.dispatch('candidate/fetchData', {
        vacancyId: this.vacancyId,
      });
    }
    if (this.candidate) {
      LogRocket.identify(this.candidate.id, {
        name: `${this.candidate.firstName} ${this.candidate.lastName}`,
        email: this.candidate.email,
        phone: this.candidate.phone,
        authType: 'candidate',
      });
    }

    if (!canRecordInterview()) {
      return null;
    }

    if (this.$route.query.token) {
      await this.$store.dispatch('candidate/changeToken', {
        token: this.$route.query.token,
        vacancyId: this.candidate.vacancyId,
      });
      await this.$store.dispatch('candidate/fetchData', { vacancyId: this.vacancyId });
    } else {
      this.$router.push({
        name: this.$route.name,
        params: { vacancyId: this.vacancyId, step: this.step },
        query: { token: localStorage.getItem(`vacancy_${this.vacancyId}_current_candidate`) },
      });
    }

    this.loading = false;

    if (!this.candidate) {
      return null;
    }

    if (this.candidate.status === candidateStatuses.PASSED_DEMO_INTERVIEW) {
      this.isDone = true;
    }

    if (this.candidate.status !== candidateStatuses.INVITED
      && this.candidate.status !== candidateStatuses.PASSED_DEMO_INTERVIEW) {
      return this.$router.push({
        name: this.$route.meta.iframe ? 'iframeInterviewResult' : 'interviewResult',
        params: {
          companyId: this.candidateVacancy.companyId,
          vacancyId: this.candidateVacancy.id,
        },
      });
    }

    if (this.candidate.interview
      && this.candidate.status !== candidateStatuses.PASSED_DEMO_INTERVIEW) {
      return this.$router.push({
        name: this.$route.meta.iframe ? 'iframeInterviewTestContinue' : 'interviewTestContinue',
        params: {
          companyId: this.candidateVacancy.companyId,
          vacancyId: this.candidateVacancy.id,
        },
      });
    }

    return null;
  },
  methods: {
    permissionsGranted() {
      this.setStep(2);
    },
    stopped() {
      if (this.$route.meta.iframe) {
        this.iframeInterviewDone = true;
      } else {
        this.isDone = true;
      }
    },
    completeIframeInterview() {
      window.parent.postMessage('huntica-interview-done', '*');
    },
    interrupted() {
      this.isInterrupted = true;
    },
    handleVacancyContainerReady({ vacancy }) {
      this.pageTitle = `${this.$t('pageTitle')} ${vacancy.title}`;
      this.setLocaleFromQuery(this.$route);
    },
    setStep(val) {
      this.currentStep = val;
      localStorage.setItem('interviewStep', val);
    },
    async setInterviewStep(val) {
      await this.createTestCandidateAndLogin();
      this.currentStep = val;
      localStorage.setItem('interviewStep', val);
    },
    async createTestCandidateAndLogin() {
      const recaptchaToken = await this.getRecaptchaToken('candidateCreate');
      const candidate = await this.$store.dispatch('candidate/createTest', {
        vacancyId: this.vacancyId,
        params: { recaptchaToken },
      });
      await this.$store.dispatch('candidate/loginByHashId', {
        hashId: candidate.hashId,
        vacancyId: this.vacancyId,
      });
      await this.$store.dispatch('candidate/fetchData', { vacancyId: this.vacancyId });
    },
  },
};
</script>
<style lang="scss" scoped>
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  background: red;
  position: relative;
  top: -10px;
}

.interview-avatar {
  width: 60px;
  height: 60px;
  margin: 5px;
  position: absolute;
  left: 15px;
  top: 15px;
  z-index: 2;
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .interview-avatar {
    left: 30px;
    top: 30px;
  }
}

.stepper-header {
  &::v-deep {
    .v-stepper__label {
      text-align: center;
    }

    .v-divider {
      border-style: dotted;
      margin-top: 37px;
    }

    .v-stepper__step {
      flex-basis: 130px;

      .v-stepper__step__step {
        background: #fff !important;
        color: map-get($grey, 'lighten-1');
        font-size: 14px !important;
      }

      &.v-stepper__step--active {
        .v-stepper__step__step {
          background: map-get($blue, 'base') !important;
          color: #fff;
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.chip {
  padding: 0 12px;
  border-radius: 5px;
  background-color: rgba(92, 187, 246, 0.2);
  font-size: 1.25rem;
}
</style>

<style lang="scss">
.interview-iframe .v-text-field--outlined .v-input__slot {
  background-color: white !important;
}
</style>

<i18n locale="en" lang="yaml">
browserListTip: "Use one of the browsers listed below:"
back: Go back
profile: personal data
cameraOn: permissions
micSettings: mic setup
cameraSettings: camera setup
interview: interview
email: email
interviewRecord: Record interview
pageTitle: Video interview for the job
close: Complete the video interview
</i18n>

<i18n locale="ru" lang="yaml">
browserListTip: >-
  Для прохождения видео-интервью необходимо воспользоваться последней
  версией одного из следующих браузеров
back: Назад
profile: профиль
cameraOn: включите камеру
micSettings: настройка микрофона
cameraSettings: настройка камеры
interview: интервью
email: почта
interviewRecord: Запись видео-интервью
pageTitle: Видео-интервью на вакансию
close: Завершить
</i18n>
